import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { PageHeader, Disclaimer } from "@/components/Common";
import { DocStatusBadge } from "@/components/StatusBadge";
import { DisclosureClassBadge, DivisionBadge, ExpiryBadge } from "@/components/VaultBadges";
import { VaultDocumentDetail } from "@/components/VaultDocumentDetail";
import { toast } from "sonner";
import { Plus, Vault, ShieldCheck, Link2 } from "lucide-react";

// Kandake Document Repository — Vault (Locked Minimum Specification, Phase 1).
// Single authoritative repository: upload once, link to many records.
// Division visibility is enforced server-side; this page only ever sees
// documents within the operator's visible division scopes.

export default function DocumentVault() {
  const [searchParams] = useSearchParams();
  const [config, setConfig] = useState(null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [division, setDivision] = useState("all");
  const [klass, setKlass] = useState("all");
  const [status, setStatus] = useState("all");
  const [dtype, setDtype] = useState("all");
  const [expiredOnly, setExpiredOnly] = useState(false);
  const [currentOnly, setCurrentOnly] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(searchParams.get("doc") || null);

  async function load() {
    setLoading(true);
    try {
      const params = {};
      if (division !== "all") params.division = division;
      if (klass !== "all") params.disclosure_class = klass;
      if (status !== "all") params.status = status;
      if (dtype !== "all") params.document_type = dtype;
      if (q.trim()) params.q = q.trim();
      if (expiredOnly) params.expired_only = true;
      if (currentOnly) params.current_only = true;
      const r = await api.get("/vault/documents", { params });
      setDocs(r.data.documents || []);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }

  useEffect(() => {
    api.get("/vault/config").then(r => setConfig(r.data)).catch(e => toast.error(errMsg(e)));
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [division, klass, status, dtype, expiredOnly, currentOnly]);

  const canWriteVault = !!config?.you_can_write;
  const statuses = useMemo(() => [...(config?.statuses || []), ...(config?.legacy_statuses || [])], [config]);

  return (
    <div data-testid="vault-page">
      <PageHeader
        title="Document Vault"
        subtitle="Kandake is the authoritative repository for rights, agreement, ownership, clearance and supporting documentation across GFMP, GFMP Sync and Kingswork. Upload once — link everywhere."
        actions={canWriteVault ? (
          <Button onClick={() => setCreateOpen(true)} data-testid="vault-new-document-btn" style={{ background: "hsl(347 72% 22%)" }}>
            <Plus className="w-4 h-4 mr-2" />New Vault Document
          </Button>
        ) : (
          <span className="text-[11px] uppercase tracking-wide text-muted-foreground" data-testid="vault-readonly-badge">Read-only access</span>
        )}
      />

      {/* Governing principle strip */}
      <Card className="mb-4 border-[hsl(40_48%_32%)]/30">
        <CardContent className="p-3 flex items-start gap-2 text-xs" data-testid="vault-governing-note">
          <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "hsl(40 48% 32%)" }} />
          <div>
            <span className="font-medium">Vault rule:</span> documents are never duplicated into separate division repositories, and nothing is automatically furnished externally. Operating Proof may be manually furnished in the ordinary course; Restricted releases require explicit operator authorization; Deal Delivery ships only after an accepted transaction.
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card className="mb-4">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="min-w-[220px] flex-1">
              <Label className="text-xs text-muted-foreground">Search</Label>
              <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Title, type, filename, notes…" data-testid="vault-search-input" />
            </div>
            <div className="min-w-[150px]">
              <Label className="text-xs text-muted-foreground">Division</Label>
              <Select value={division} onValueChange={setDivision}>
                <SelectTrigger data-testid="vault-division-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All visible</SelectItem>
                  {(config?.your_visible_divisions || []).map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[170px]">
              <Label className="text-xs text-muted-foreground">Class</Label>
              <Select value={klass} onValueChange={setKlass}>
                <SelectTrigger data-testid="vault-class-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All classes</SelectItem>
                  {(config?.disclosure_classes || []).map(c => <SelectItem key={c} value={c}>{c.replace("_", " ")}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[170px]">
              <Label className="text-xs text-muted-foreground">Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="vault-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {statuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[210px]">
              <Label className="text-xs text-muted-foreground">Type</Label>
              <Select value={dtype} onValueChange={setDtype}>
                <SelectTrigger data-testid="vault-type-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  {(config?.document_types || []).map(t => <SelectItem key={t.name} value={t.name}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 pb-2 text-xs cursor-pointer">
              <Switch checked={expiredOnly} onCheckedChange={setExpiredOnly} data-testid="vault-expired-toggle" />Expired only
            </label>
            <label className="flex items-center gap-2 pb-2 text-xs cursor-pointer">
              <Switch checked={currentOnly} onCheckedChange={setCurrentOnly} data-testid="vault-current-toggle" />Current versions
            </label>
            <span className="text-xs text-muted-foreground pb-2 ml-auto" data-testid="vault-count">{docs.length} document{docs.length === 1 ? "" : "s"}</span>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0 overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Type / Title</TableHead>
                <TableHead>Class</TableHead>
                <TableHead>Division</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Applicability</TableHead>
                <TableHead>Links</TableHead>
                <TableHead>Ver</TableHead>
                <TableHead>Expiry</TableHead>
                <TableHead>Updated</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={9} className="text-center py-10 text-muted-foreground">Loading vault…</TableCell></TableRow>
              )}
              {!loading && docs.length === 0 && (
                <TableRow><TableCell colSpan={9} className="text-center py-10 text-muted-foreground" data-testid="vault-empty-state">
                  <Vault className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  No vault documents match. {canWriteVault ? "Use New Vault Document to request or upload one." : ""}
                </TableCell></TableRow>
              )}
              {!loading && docs.map(d => {
                const links = (d.links || []).filter(l => !l.legacy);
                return (
                  <TableRow key={d.id} className="cursor-pointer hover:bg-secondary/40" onClick={() => setSelectedId(d.id)} data-testid={`vault-row-${d.id}`}>
                    <TableCell className="max-w-[240px]">
                      <div className="font-medium text-sm truncate">{d.document_type}</div>
                      {d.title && <div className="text-xs text-muted-foreground truncate">{d.title}</div>}
                    </TableCell>
                    <TableCell><DisclosureClassBadge value={d.disclosure_class} /></TableCell>
                    <TableCell><DivisionBadge value={d.division_scope} /></TableCell>
                    <TableCell><DocStatusBadge status={d.document_status} /></TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{d.applicability}</TableCell>
                    <TableCell>
                      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground" data-testid={`vault-links-count-${d.id}`}>
                        <Link2 className="w-3 h-3" />{links.length}
                      </span>
                    </TableCell>
                    <TableCell className="font-mono text-xs">v{d.version}{!d.is_current && <span className="text-muted-foreground"> ·old</span>}</TableCell>
                    <TableCell><ExpiryBadge isExpired={d.is_expired} expirationDate={d.expiration_date} /></TableCell>
                    <TableCell className="font-mono text-[11px] text-muted-foreground whitespace-nowrap">{(d.updated_at || "").slice(0, 10)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Disclaimer>
        Kandake stores it · Kandake verifies it · the appropriate operating company sees it · external parties receive only what an authorized operator deliberately furnishes.
      </Disclaimer>

      {/* Create dialog */}
      {config && (
        <VaultCreateDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          config={config}
          onCreated={(id) => { setCreateOpen(false); load(); setSelectedId(id); }}
        />
      )}

      {/* Detail sheet */}
      <Sheet open={!!selectedId} onOpenChange={(o) => !o && setSelectedId(null)}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="vault-detail-sheet">
          <SheetHeader>
            <SheetTitle>Vault Document</SheetTitle>
          </SheetHeader>
          <div className="mt-4">
            {selectedId && (
              <VaultDocumentDetail
                docId={selectedId}
                config={config}
                canWriteVault={canWriteVault}
                onChanged={(newId) => { load(); if (newId) setSelectedId(newId); }}
              />
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function VaultCreateDialog({ open, onOpenChange, config, onCreated }) {
  const empty = {
    document_type: "",
    title: "",
    division_scope: "SHARED",
    disclosure_class: "",
    applicability: "Required If Applicable",
    document_status: "Requested",
    expiration_date: "",
    notes: "",
  };
  const [f, setF] = useState(empty);
  const [linkTrack, setLinkTrack] = useState("none");
  const [tracks, setTracks] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setF(empty);
      setLinkTrack("none");
      api.get("/tracks").then(r => setTracks(r.data || [])).catch(() => {});
    }
    // eslint-disable-next-line
  }, [open]);

  const typeInfo = (config.document_types || []).find(t => t.name === f.document_type);
  const effectiveClass = f.disclosure_class || typeInfo?.default_disclosure_class || "RESTRICTED";

  async function submit() {
    if (!f.document_type) { toast.error("Document type is required"); return; }
    setSaving(true);
    try {
      const body = {
        ...f,
        disclosure_class: effectiveClass,
        links: linkTrack !== "none" ? [{ record_type: "track", record_id: linkTrack }] : [],
      };
      const r = await api.post("/vault/documents", body);
      toast.success("Vault document created");
      onCreated(r.data.id);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>New Vault Document</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Document type <span className="text-destructive">*</span></Label>
            <Select value={f.document_type} onValueChange={(v) => setF(x => ({ ...x, document_type: v, disclosure_class: "" }))}>
              <SelectTrigger data-testid="vault-create-type-select"><SelectValue placeholder="Choose type…" /></SelectTrigger>
              <SelectContent className="max-h-[280px]">
                {(config.document_types || []).map(t => <SelectItem key={t.name} value={t.name}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {f.document_type && (
              <div className="mt-1.5 flex items-center gap-2 text-[11px] text-muted-foreground">
                Default class: <DisclosureClassBadge value={typeInfo?.default_disclosure_class} />
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs">Title / description</Label>
            <Input value={f.title} onChange={(e) => setF(x => ({ ...x, title: e.target.value }))} placeholder="Optional label, e.g. writer or deal name" data-testid="vault-create-title-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Division scope</Label>
              <Select value={f.division_scope} onValueChange={(v) => setF(x => ({ ...x, division_scope: v }))}>
                <SelectTrigger data-testid="vault-create-division-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(config.your_visible_divisions || []).map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Disclosure class</Label>
              <Select value={effectiveClass} onValueChange={(v) => setF(x => ({ ...x, disclosure_class: v }))}>
                <SelectTrigger data-testid="vault-create-class-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(config.disclosure_classes || []).map(c => <SelectItem key={c} value={c}>{c.replace("_", " ")}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Applicability</Label>
              <Select value={f.applicability} onValueChange={(v) => setF(x => ({ ...x, applicability: v }))}>
                <SelectTrigger data-testid="vault-create-applicability-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(config.applicability_options || []).map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Expiration date</Label>
              <Input type="date" value={f.expiration_date} onChange={(e) => setF(x => ({ ...x, expiration_date: e.target.value }))} data-testid="vault-create-expiration-input" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Link to track (optional)</Label>
            <Select value={linkTrack} onValueChange={setLinkTrack}>
              <SelectTrigger data-testid="vault-create-track-select"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-[240px]">
                <SelectItem value="none">No link yet</SelectItem>
                {tracks.map(t => <SelectItem key={t.id} value={t.id}>{t.track_title} · {t.track_id}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea value={f.notes} onChange={(e) => setF(x => ({ ...x, notes: e.target.value }))} rows={2} maxLength={2000} data-testid="vault-create-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="vault-create-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} style={{ background: "hsl(347 72% 22%)" }} data-testid="vault-create-submit">{saving ? "Creating…" : "Create document"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
